import Phaser from "phaser";

import { ARENA_ART, TROPHY_HALL_ART, WORKSHOP_ENERGY_ART } from "../config/artAssets";

export type ArenaCatalogCallbacks = {
  onReady?(): void;
};

export class ArenaCatalogScene extends Phaser.Scene {
  readonly #callbacks: ArenaCatalogCallbacks;
  #reducedMotion: boolean;
  #background!: Phaser.GameObjects.Image;
  #shade!: Phaser.GameObjects.Rectangle;
  #portals!: Phaser.GameObjects.Container;
  #glows: Phaser.GameObjects.Arc[] = [];
  #emblems: Phaser.GameObjects.Image[] = [];
  #ambientTweens: Phaser.Tweens.Tween[] = [];

  constructor(callbacks: ArenaCatalogCallbacks, reducedMotion: boolean) {
    super({ key: "ArenaCatalog" });
    this.#callbacks = callbacks;
    this.#reducedMotion = reducedMotion;
  }

  preload(): void {
    this.load.image(ARENA_ART.background.key, ARENA_ART.background.url);
    this.load.image(ARENA_ART.ball.key, ARENA_ART.ball.url);
    this.load.image(WORKSHOP_ENERGY_ART.sigil.key, WORKSHOP_ENERGY_ART.sigil.url);
    this.load.image(TROPHY_HALL_ART.seasonCrown.key, TROPHY_HALL_ART.seasonCrown.url);
  }

  create(): void {
    this.#background = this.add.image(0, 0, ARENA_ART.background.key);
    this.#shade = this.add.rectangle(0, 0, 10, 10, 0x062b35, 0.34).setOrigin(0);
    const assets = [ARENA_ART.ball, WORKSHOP_ENERGY_ART.sigil, TROPHY_HALL_ART.seasonCrown] as const;
    this.#glows = assets.map((_, index) =>
      this.add
        .circle((index - 1) * 94, 0, index === 0 ? 54 : 44, index === 0 ? 0xffc970 : 0x58ece9, index === 0 ? 0.24 : 0.13)
        .setBlendMode(Phaser.BlendModes.ADD),
    );
    this.#emblems = assets.map((asset, index) =>
      this.add.image((index - 1) * 94, 0, asset.key).setAlpha(index === 0 ? 1 : 0.58),
    );
    this.#portals = this.add.container(0, 0, [...this.#glows, ...this.#emblems]);
    this.scale.on(Phaser.Scale.Events.RESIZE, this.#layout, this);
    this.#layout({ width: this.scale.width, height: this.scale.height } as Phaser.Structs.Size);
    this.#restartMotion();
    this.#callbacks.onReady?.();
  }

  setReducedMotion(reducedMotion: boolean): void {
    if (this.#reducedMotion === reducedMotion) return;
    this.#reducedMotion = reducedMotion;
    if (this.sys.isActive()) this.#restartMotion();
  }

  #layout(size: Phaser.Structs.Size): void {
    const width = Math.round(size.width);
    const height = Math.round(size.height);
    if (width <= 0 || height <= 0) return;
    const backgroundScale = Math.max(width / ARENA_ART.background.width, height / ARENA_ART.background.height);
    this.#background.setPosition(width / 2, height / 2).setScale(backgroundScale);
    this.#shade.setSize(width, height);
    this.#portals.setPosition(width / 2, Math.min(height * 0.4, 260));

    const ballScale = Math.min(0.34, 76 / ARENA_ART.ball.width);
    const sigilScale = Math.min(0.19, 82 / WORKSHOP_ENERGY_ART.sigil.height);
    const crownScale = Math.min(0.16, 84 / TROPHY_HALL_ART.seasonCrown.height);
    this.#emblems[0]?.setScale(ballScale);
    this.#emblems[1]?.setScale(sigilScale);
    this.#emblems[2]?.setScale(crownScale);
  }

  #restartMotion(): void {
    this.#ambientTweens.forEach((tween) => tween.stop());
    this.#ambientTweens = [];
    this.#glows.forEach((glow) => glow.setScale(1).setAlpha(glow === this.#glows[0] ? 0.24 : 0.13));
    this.#emblems.forEach((emblem) => emblem.setY(0).setAngle(0));
    if (this.#reducedMotion) return;
    this.#emblems.forEach((emblem, index) => {
      this.#ambientTweens.push(
        this.tweens.add({
          targets: emblem,
          y: index === 1 ? -5 : -3,
          angle: index === 0 ? 5 : index === 1 ? -2 : 2,
          duration: 2_200 + index * 320,
          delay: index * 130,
          yoyo: true,
          repeat: -1,
          ease: "Sine.easeInOut",
        }),
      );
    });
    this.#glows.forEach((glow, index) => {
      this.#ambientTweens.push(
        this.tweens.add({
          targets: glow,
          alpha: index === 0 ? 0.4 : 0.24,
          scale: 1.12,
          duration: 1_700 + index * 240,
          yoyo: true,
          repeat: -1,
          ease: "Sine.easeInOut",
        }),
      );
    });
  }
}
